import isEqual from "lodash/isEqual";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Redirect, useParams } from "react-router-dom";

import Alert from "veneer/Alert";
import Button from "veneer/Button";
import Stack from "veneer/Stack";

import { Application } from "cortex/ui/src/fleet/view/application/model";
import { getStrategyContentAppId } from "cortex/ui/src/fleet/view/strategy/utils";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { Network } from "cortex/ui/src/network/model";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas/actions";
import {
  applicationsSelector,
  applicationsStoresLoadedSelector,
  getAllByIdExceptSchedulers,
  getSingleZoneSchedulerAppId,
  installedAppsSelector,
} from "cortex/ui/src/redux-store/applications/selectors";
import { getAuthUser } from "cortex/ui/src/redux-store/auth/selectors";
import { EDIT_DEVICE } from "cortex/ui/src/redux-store/devices/actions";
import {
  deviceStoresLoadedSelector,
  devicesSelector,
  getDeviceTags,
  getName,
  getTagsWithIds,
  isSystemManaged,
} from "cortex/ui/src/redux-store/devices/selectors";
import {
  networksSelector,
  networksStoresLoadedSelector,
} from "cortex/ui/src/redux-store/networks/selectors";
import {
  makeGetAllAsArray,
  makeGetSelectOptions,
} from "cortex/ui/src/redux-store/selectors";
import {
  currentDeviceUnloadAction,
  fetchDeviceAction,
} from "cortex/ui/src/redux-store/ui-state/current-device/actions";
import {
  currentDeviceSelector,
  currentDeviceStoresLoadedSelector,
} from "cortex/ui/src/redux-store/ui-state/current-device/selectors";
import { isReadonly } from "cortex/ui/src/redux-store/users/selectors";
import BackButton from "cortex/ui/src/ui/button";
import { NavLoader } from "cortex/ui/src/ui/loader";
import Notification from "cortex/ui/src/ui/notification";
import { Option } from "cortex/ui/src/ui/table/types";
import { getBreadcrumbs } from "cortex/ui/src/util/breadcrumbs";
import type { ResponseLike } from "cortex/ui/src/util/response";
import { getErrorMessage, getFormErrors } from "cortex/ui/src/util/response";
import { Container, Panel, StickyPageActions } from "cortex/ui/src/view/layout";

import DeviceEditForm from "./component/DeviceEditForm";
import { Device, FormData } from "./model";

const getAllAppsAsArray = makeGetAllAsArray<Application>();
const getNetworkSelectOptions = makeGetSelectOptions<Network>();

type DeviceFallbackContent = NonNullable<FormData["fallbackContents"]>;
type TagOption = Option<number>;

function normalizeTagOptions(
  tags: ReadonlyArray<{ label?: string | undefined; value: number }>,
): ReadonlyArray<TagOption> {
  return tags.flatMap((tag) =>
    tag.label ? [{ label: tag.label, value: tag.value }] : [],
  );
}

interface DeviceEditFormState extends FormData {
  deviceOperateTime: string | null;
  fallbackContents: DeviceFallbackContent;
  hasOverrideNetwork: boolean;
  hasSystemStrategy: boolean;
  location: string;
  name: string;
  selectedApplication: Option<number> | null;
  selectedNetwork: Option<number> | null;
  selectedTags: ReadonlyArray<Option>;
  testing: boolean;
}

interface DeviceEditPayload {
  fallbackContents: string;
  id: number;
  location: string;
  mainApp: string;
  name: string;
  networkId: number | null;
  operateParams: string | null;
  tags: string[];
  testing: boolean;
}

interface SourceRef {
  applications: ReadonlyArray<Application>;
  device: Device | null;
  network: Network | undefined;
}

export function getDefaultFormData(): DeviceEditFormState {
  return {
    deviceOperateTime: null,
    fallbackContents: [],
    hasOverrideNetwork: false,
    hasSystemStrategy: false,
    location: "",
    name: "",
    selectedApplication: null,
    selectedNetwork: null,
    selectedTags: [],
    testing: false,
  };
}

export function getDeviceFormData(
  device: Device | null,
  network: Network | undefined,
  applications: ReadonlyArray<Application>,
): DeviceEditFormState {
  const details = getDefaultFormData();

  if (!device || !network) {
    return details;
  }

  const hasDeviceOperateTime = device.operate_time !== null;
  const appId = getStrategyContentAppId(device);
  const application =
    appId === null
      ? null
      : (applications.find((currentApp) => currentApp.id === appId) ?? null);

  return {
    deviceOperateTime: hasDeviceOperateTime
      ? device.operate_time
      : (network.device_operate_time ?? null),
    fallbackContents: (device.strategy_content?.priorities?.[1] ??
      []) as DeviceFallbackContent,
    hasOverrideNetwork: hasDeviceOperateTime,
    hasSystemStrategy: appId !== null,
    location: device.location,
    name: device.name,
    selectedApplication: application
      ? {
          label: application.display_name,
          value: application.id,
        }
      : null,
    selectedNetwork: {
      label: network.name,
      value: network.id,
    },
    selectedTags: normalizeTagOptions(getDeviceTags(device)),
    testing: device.testing,
  };
}

function getApplicationOptions(
  applications: ReadonlyArray<Application>,
): ReadonlyArray<Option<number>> {
  return applications
    .map((application) => ({
      label: application.display_name,
      value: application.id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function validateSystemStrategy(formData: DeviceEditFormState): string {
  if (!formData.hasSystemStrategy) {
    return "";
  }

  const hasApp = Boolean(formData.selectedApplication);
  const hasFallback = formData.fallbackContents.length > 0;

  if (!hasApp && !hasFallback) {
    return "You must select an app and at least one fallback creative for your device to play. To use its network defaults, turn Override Network Content Source off.";
  }

  if (!hasApp && hasFallback) {
    return "You must select an app for your device to play.";
  }

  if (hasApp && !hasFallback) {
    return "At least one fallback creative must be selected.";
  }

  return "";
}

export function getDeviceEditPayload(
  device: Device,
  formData: DeviceEditFormState,
  schedulerAppId?: number,
): DeviceEditPayload {
  return {
    fallbackContents: formData.hasSystemStrategy
      ? JSON.stringify(formData.fallbackContents)
      : "",
    id: device.id,
    location: formData.location,
    mainApp:
      formData.selectedApplication && formData.hasSystemStrategy
        ? JSON.stringify({
            appId: formData.selectedApplication.value,
            scheduler: schedulerAppId,
          })
        : "",
    name: formData.name,
    networkId: formData.selectedNetwork?.value ?? null,
    operateParams: formData.hasOverrideNetwork
      ? (formData.deviceOperateTime ?? null)
      : null,
    tags: formData.selectedTags.map((tag) => tag.label),
    testing: formData.testing,
  };
}

export default function DeviceEdit() {
  const { deviceId } = useParams<{ deviceId: string }>();
  const dispatch = useAppDispatch();
  const device = useAppSelector(currentDeviceSelector) as Device | null;
  const currentDeviceStoresLoaded = useAppSelector(
    currentDeviceStoresLoadedSelector,
  );
  const allAppsById = useAppSelector(applicationsSelector);
  const applicationsStoresLoaded = useAppSelector(
    applicationsStoresLoadedSelector,
  );
  const devices = useAppSelector(devicesSelector);
  const devicesStoresLoaded = useAppSelector(deviceStoresLoadedSelector);
  const networks = useAppSelector(networksSelector);
  const networksStoresLoaded = useAppSelector(networksStoresLoadedSelector);
  const user = useAppSelector(getAuthUser);
  const storesLoaded = useMemo(
    () =>
      applicationsStoresLoaded &&
      currentDeviceStoresLoaded &&
      devicesStoresLoaded &&
      networksStoresLoaded,
    [
      applicationsStoresLoaded,
      currentDeviceStoresLoaded,
      devicesStoresLoaded,
      networksStoresLoaded,
    ],
  );
  const readonly = isReadonly(user);
  const appById = getAllByIdExceptSchedulers({
    byId: allAppsById,
  });
  const schedulerAppId = getSingleZoneSchedulerAppId(allAppsById) ?? 132;
  const apps = getAllAppsAsArray(appById) as ReadonlyArray<Application>;
  const applications = installedAppsSelector(
    apps,
  ) as ReadonlyArray<Application>;
  const network = device?.network_id
    ? (networks[device.network_id] as Network | undefined)
    : undefined;
  const deviceTagSelectOptions = useMemo(
    () => normalizeTagOptions(getTagsWithIds(devices)),
    [devices],
  );
  const networkOptions = useMemo(
    () => getNetworkSelectOptions(networks) as ReadonlyArray<Option<number>>,
    [networks],
  );
  const applicationOptions = useMemo(
    () => getApplicationOptions(applications),
    [applications],
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [formData, setFormData] = useState<DeviceEditFormState>(
    getDefaultFormData(),
  );
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const previousSourceRef = useRef<SourceRef>({
    applications: [],
    device: null,
    network: undefined,
  });

  useEffect(() => {
    if (!deviceId) {
      return;
    }

    dispatch(
      fetchDeviceAction({
        serviceArgs: {
          deviceId,
        },
      }),
    );

    return () => {
      dispatch(currentDeviceUnloadAction);
    };
  }, [deviceId, dispatch]);

  useEffect(() => {
    const sourceChanged =
      !isEqual(previousSourceRef.current.device, device) ||
      !isEqual(previousSourceRef.current.network, network) ||
      !isEqual(previousSourceRef.current.applications, applications);

    if (!sourceChanged) {
      return;
    }

    setFormData(getDeviceFormData(device, network, applications));
    previousSourceRef.current = {
      applications,
      device,
      network,
    };
  }, [applications, device, network]);

  const editDevice = (payload: DeviceEditPayload) => {
    return createPromiseApiAction(
      dispatch,
      EDIT_DEVICE,
      [
        payload.id,
        payload.fallbackContents,
        payload.mainApp,
        payload.name,
        payload.networkId,
        payload.testing,
        payload.location,
        payload.tags,
        payload.operateParams,
      ],
      "",
    );
  };

  const handleSubmit = async () => {
    if (!device) {
      return;
    }

    setSubmitted(true);

    const validationMessage = validateSystemStrategy(formData);
    if (validationMessage) {
      setErrorMessage(validationMessage);
      setSubmitted(false);
      window.scrollTo(0, 0);
      return;
    }

    const nextFormData = formData.hasSystemStrategy
      ? formData
      : {
          ...formData,
          fallbackContents: [],
          selectedApplication: null,
        };

    if (!formData.hasSystemStrategy) {
      setFormData(nextFormData);
    }

    try {
      await editDevice(
        getDeviceEditPayload(device, nextFormData, schedulerAppId),
      );
      setErrorMessage("");
      setFormErrors({});
      setSuccessMessage("Device details are updated.");
    } catch (e) {
      const error = e as Error | ResponseLike | null;
      const responseError = error as ResponseLike | undefined;
      setFormErrors(
        getFormErrors(responseError).formErrors as Record<string, string>,
      );
      setErrorMessage(getErrorMessage(error ?? undefined));
      setSuccessMessage("");
    } finally {
      setSubmitted(false);
      window.scrollTo(0, 0);
    }
  };

  if (!deviceId) {
    return <Redirect to="/404" />;
  }

  const deviceName = device ? getName(device) : "Device";
  const systemManaged = isSystemManaged(device ?? undefined);
  const breadcrumbs = device
    ? getBreadcrumbs([
        { label: "Devices", target: "/devices" },
        { label: device.name, target: `/devices/${device.device_id}` },
      ])
    : null;

  if (!storesLoaded) {
    return <NavLoader.Proxy />;
  }

  if (!device || !network || device.device_id !== deviceId) {
    return <Redirect to="/404" />;
  }

  return (
    <Container
      breadcrumbs={breadcrumbs ?? undefined}
      hideBox
      p={2}
      title={`Edit ${deviceName}`}
    >
      <Panel spacing={2}>
        {systemManaged && (
          <Alert dataTestId="ad-platform-sync-alert" dense severity="info">
            This device is synced with the Ad Platform, therefore certain fields
            will not be editable.
          </Alert>
        )}
        <Notification
          error={errorMessage}
          success={successMessage}
          successChildren={
            <Link to={`/devices/${device.device_id}`}>View Device</Link>
          }
        />
        <DeviceEditForm
          applicationOptions={applicationOptions}
          deviceTagOptions={deviceTagSelectOptions}
          errors={formErrors}
          networkOptions={networkOptions}
          onChange={(nextValue) => {
            setFormData(nextValue as DeviceEditFormState);
          }}
          readonly={readonly}
          systemManaged={systemManaged}
          value={formData}
        />
        <StickyPageActions>
          <Stack alignItems="center" direction="row" gap={1}>
            <BackButton />
            <Button disabled={submitted} onClick={handleSubmit}>
              Save
            </Button>
          </Stack>
        </StickyPageActions>
      </Panel>
    </Container>
  );
}
